import { Suspense, lazy } from 'react'
import { Loader2 } from 'lucide-react'
import { Route, Routes } from 'react-router-dom'
import { Layout } from './components/Layout'
import { RequireAdmin } from './components/RequireAdmin'
import { Login } from './pages/Login'
import { NoAccess } from './pages/NoAccess'
import { useSession } from './lib/session'

/**
 * Every route is lazy.
 *
 * Recharts is ~140 KB gzipped and only three screens draw a chart, but a single
 * bundle made a teacher who opened the roster download all of it. Splitting here
 * means the charting code arrives with the first screen that actually charts.
 *
 * Login and NoAccess are static imports on purpose: they are what an unauthenticated
 * visitor sees, and making them wait on a second round-trip is the one place a
 * spinner is most obviously wrong.
 */
const Overview = lazy(() => import('./pages/Overview').then((m) => ({ default: m.Overview })))
const Groups = lazy(() => import('./pages/Groups').then((m) => ({ default: m.Groups })))
const GroupDetail = lazy(() =>
  import('./pages/GroupDetail').then((m) => ({ default: m.GroupDetail })),
)
const StudentDetail = lazy(() =>
  import('./pages/StudentDetail').then((m) => ({ default: m.StudentDetail })),
)
const Coverage = lazy(() => import('./pages/Coverage').then((m) => ({ default: m.Coverage })))
const Roster = lazy(() => import('./pages/admin/Roster').then((m) => ({ default: m.Roster })))
const RosterImport = lazy(() =>
  import('./pages/admin/RosterImport').then((m) => ({ default: m.RosterImport })),
)
const Structure = lazy(() =>
  import('./pages/admin/Structure').then((m) => ({ default: m.Structure })),
)
const SchoolSettings = lazy(() =>
  import('./pages/admin/SchoolSettings').then((m) => ({ default: m.SchoolSettings })),
)
const Billing = lazy(() => import('./pages/admin/Billing').then((m) => ({ default: m.Billing })))

function Spinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center text-faint">
      <Loader2 size={22} className="animate-spin" />
    </div>
  )
}

export default function App() {
  const { loading, isSignedIn, staff } = useSession()

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg text-muted">
        <Loader2 size={22} className="animate-spin" />
      </div>
    )
  }

  if (!isSignedIn) return <Login />

  // A valid account with no staff membership gets its own screen, not a login error:
  // the credentials were correct.
  if (!staff) return <NoAccess />

  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Overview />} />
          <Route path="/groups" element={<Groups />} />
          <Route path="/groups/:groupId" element={<GroupDetail />} />
          <Route path="/groups/:groupId/coverage" element={<Coverage />} />
          <Route path="/students/:studentId" element={<StudentDetail />} />
          <Route path="/coverage" element={<Coverage />} />

          <Route path="/school/roster" element={<RequireAdmin><Roster /></RequireAdmin>} />
          <Route path="/school/roster/import" element={<RequireAdmin><RosterImport /></RequireAdmin>} />
          <Route path="/school/structure" element={<RequireAdmin><Structure /></RequireAdmin>} />
          <Route path="/school/settings" element={<RequireAdmin><SchoolSettings /></RequireAdmin>} />
          <Route path="/school/billing" element={<RequireAdmin><Billing /></RequireAdmin>} />

          {/* The catalogue builder used to live here under `/platform/*`, gated by
              RequireAdmin — which checks *school* admin. Any school's admin could
              therefore open the platform-wide curriculum builder. It is a platform
              operation on shared content, not a school's own data, so it moved to
              the admin app where `platform_admins` gates it. */}
        </Routes>
      </Suspense>
    </Layout>
  )
}
